<template>
  <div
    class="report-control"
  >
    <div class="text-subtitle2">
      {{ $_.startCase(control) }}
    </div>
    <div
      v-if="control === 'ticket_time_mode'"
    >
      <q-btn-toggle
        v-model="reportOptions.ticket_time_mode"
        :disable="disable"
        class="q-my-xs"
        size="sm"
        outline
        toggle-color="primary"
        :options="[
          { label: 'Showing Time', value: 'showing' },
          { label: 'Purchased At', value: 'purchased' },
          { label: 'Maturing', value: 'maturing' },
          { label: 'Retroactive Effective', value: 'retroactive_effective' },
          { label: 'Retroactive Placed', value: 'retroactive_placed' },
          { label: 'Current', value: 'current' },
          { label: 'Future', value: 'future' },
        ]"
      />
    </div>
    <div
      v-else-if="control === 'ticket_value_mode'"
    >
      <q-btn-toggle
        v-model="reportOptions.ticket_value_mode"
        :disable="disable"
        class="q-my-xs"
        size="sm"
        outline
        toggle-color="primary"
        :options="[
          { label: 'Paid', value: 'paid' },
          { label: 'Reported', value: 'reported' }
        ]"
      />
    </div>
    <div
      v-else-if="control === 'dispute_time_mode'"
    >
      <q-btn-toggle
        v-model="reportOptions.dispute_time_mode"
        :disable="disable"
        class="q-my-xs"
        size="sm"
        outline
        toggle-color="primary"
        :options="[
          { label: 'Purchased At', value: 'paid_at' },
          { label: 'Disputed Created At', value: 'disputed_at' },
          { label: 'Dispute Closed At', value: 'dispute_decided_at' }
        ]"
      />
    </div>
    <q-toggle
      v-else-if="control === 'include_charts'"
      v-model="reportOptions.include_charts"
      label="Charts"
    />
    <q-select
      v-else-if="control === 'studio'"
      v-model="reportOptions.studio_id"
      :disable="disable"
      clearable
      style="min-width: 150px; max-width: 250px;"
      label="Studio"
      emit-value
      map-options
      option-value="id"
      option-label="name"
      :options="$_.map(studios.data, s => $_.pick(s, ['id', 'name']))"
    />
    <q-select
      v-else-if="control === 'item_class'"
      v-model="reportOptions.item_class_id"
      :disable="disable"
      clearable
      style="min-width: 150px; max-width: 250px;"
      label="Item Class"
      emit-value
      map-options
      option-value="id"
      option-label="name"
      :options="$_.map(itemClasses.data, s => $_.pick(s, ['id', 'name']))"
    />
    <q-select
      v-else-if="control === 'inventory_recipe'"
      v-model="reportOptions.inventory_recipe_id"
      :disable="disable"
      clearable
      style="min-width: 150px; max-width: 250px;"
      label="Inventory Recipe"
      emit-value
      map-options
      option-value="id"
      option-label="name"
      :options="$_.map(inventoryRecipes.data, s => $_.pick(s, ['id', 'name']))"
    />
    <q-select
      v-else-if="control === 'inventory_item'"
      v-model="reportOptions.inventory_item_id"
      :disable="disable"
      clearable
      style="min-width: 150px; max-width: 250px;"
      label="Inventory Item"
      emit-value
      map-options
      option-value="id"
      option-label="name"
      :options="$_.map(inventoryItems.data, s => $_.pick(s, ['id', 'name']))"
    />
    <q-select
      v-else-if="control === 'title_class'"
      v-model="reportOptions.title_class_id"
      :disable="disable"
      clearable
      style="min-width: 150px; max-width: 250px;"
      label="Title Class"
      emit-value
      map-options
      option-value="id"
      option-label="name"
      :options="$_.map(titleClasses.data, s => $_.pick(s, ['id', 'name']))"
    />
    <q-select
      v-else-if="control === 'configuration_type'"
      v-model="reportOptions.configuration_type"
      :disable="disable"
      clearable
      style="min-width: 150px; max-width: 250px;"
      label="Configuration Type"
      map-options
      :option-label="(option) => $_.startCase(option)"
      :options="CONFIGURATION_MODELS"
    />
    <q-select
      v-else-if="control === 'configuration_audit_type'"
      v-model="reportOptions.configuration_audit_type"
      :disable="disable"
      clearable
      style="min-width: 150px; max-width: 250px;"
      label="Configuration Audit Type"
      map-options
      :option-label="(option) => $_.startCase(option)"
      :options="CONFIGURATION_AUDIT_MODELS"
    />
    <q-select
      v-else-if="control === 'feature_name'"
      v-model="reportOptions.feature_name"
      :disable="disable"
      clearable
      style="min-width: 150px; max-width: 250px;"
      label="Feature Name"
      map-options
      :option-label="(option) => $_.startCase(option)"
      :options="FEATURE_NAMES"
    />
    <showing-badge-select
      v-else-if="control === 'group_by_showing_badge_ids'"
      :model="reportOptions"
      class="col"
      style="min-width: 150px; max-width: 250px;"
      label="Group By Showing Badges"
      multiple
      field-name="group_by_showing_badge_ids"
      :disable="disable"
    />
    <div
      v-else-if="control === 'max_wait_time'"
    >
      <q-slider
        v-model.number="control.value"
        :disable="disable"
        label
        type="number"
        :min="0"
        :max="30"
        :step="5"
        debounce="300"
      />
    </div>
    <div
      v-else-if="control === 'max_employees'"
    >
      <q-slider
        v-model.number="control.value"
        :disable="disable"
        label
        type="number"
        :min="1"
        :max="10"
        debounce="300"
      />
    </div>
    <q-input
      v-else-if="control === 'date_range'"
      :disable="disable"
      :model-value="(dateRange && dateRange.from && IsoDate.parse(dateRange.from).i18n('shortDate') || dateRange && IsoDate.parse(dateRange).i18n('shortDate') || '') + (dateRange && dateRange.to ? ' to ' : '') + (dateRange && dateRange.to && IsoDate.parse(dateRange.to).i18n('shortDate') || '')"
      style="min-width: 250px;"
      label="Range"
      @click="showCalendar = true"
    >
      <template #append>
        <q-icon
          name="event"
          class="cursor-pointer"
        >
          <q-popup-proxy
            v-model="showCalendar"
            transition-show="scale"
            transition-hide="scale"
          >
            <q-date
              v-model="dateRange"
              mask="YYYY-MM-DD"
              range
            />
          </q-popup-proxy>
        </q-icon>
      </template>
    </q-input>
    <div
      v-else-if="control === 'date'"
      class="row"
    >
      <iso-date-input
        v-model="reportOptions.date"
        emit-string="nullify-blank"
        :disable="disable"
        label="Date"
      />
      <div class="q-ml-sm">
        <q-btn
          :disable="disable"
          style="display: block"
          class="q-mb-sm"
          icon="add"
          size="sm"
          dense
          @click="reportOptions.date = formatDate(addToDate(reportOptions.date + 'T00:00', { days: 1 }), 'YYYY-MM-DD')"
        />
        <q-btn
          :disable="disable"
          style="display: block"
          icon="remove"
          size="sm"
          dense
          @click="reportOptions.date = formatDate(addToDate(reportOptions.date + 'T00:00', { days: -1 }), 'YYYY-MM-DD')"
        />
      </div>
    </div>
    <div
      v-else-if="control === 'chart_type'"
    >
      <q-btn-toggle
        v-model="reportOptions.chart_type"
        :disable="disable"
        class="q-my-xs"
        size="xs"
        outline
        toggle-color="primary"
        :options="report.optionsForSelect('chart_type')"
      />
    </div>
    <div
      v-else-if="control === 'interval'"
    >
      <q-btn-toggle
        v-model="reportOptions.interval"
        :disable="disable"
        class="q-my-xs"
        size="xs"
        outline
        toggle-color="primary"
        :options="report.optionsForSelect('interval')"
      />
    </div>
    <q-select
      v-else-if="control === 'series_name'"
      v-model="reportOptions.series_name"
      :disable="disable"
      style="min-width: 150px; max-width: 250px;"
      label="Data Set"
      :options="report.possibleOptionsValues[control]"
    />
    <q-select
      v-else-if="control === 'data_source_name'"
      id="data_source_name"
      v-model="reportOptions.data_source_name"
      :disable="disable"
      style="min-width: 150px; max-width: 250px;"
      label="Data Set"
      :options="dataSourceNameOptionsAndSeparators(dataSourceSearchValue)"
      option-value="name"
      emit-value
      use-input
      input-debounce="0"
      @filter="(val, update) => filterSearchValue(val, update, 'dataSourceSearchValue')"
    >
      <template #option="scope">
        <q-item
          v-if="scope.opt.isSeparator"
          :key="scope.opt.label"
          class="q-py-sm text-uppercase constrain-width-q-item bg-grey-2 text-subtitle2"
          dense
        >
          {{ scope.opt.label }}
        </q-item>
        <q-item
          v-else
          :key="scope.opt.key"
          class="constrain-width-q-item"
          :class="$_.includes(report.possibleOptionsValues.data_source_name_options, scope.opt.value) ? 'bg-grey-4' : ''"
          v-bind="scope.itemProps"
        >
          <q-item-section>
            <q-item-label>
              {{ scope.opt.label }}
            </q-item-label>
            <q-item-label
              v-if="scope.opt.description"
              caption
            >
              {{ scope.opt.description }}
            </q-item-label>
          </q-item-section>
          <q-item-section
            avatar
          >
            <div
              class="text-grey-8 q-gutter-sm"
            >
              <q-icon
                v-if="scope.opt.hourInterval"
                name="schedule"
                size="sm"
              >
                <q-tooltip>
                  This data set uses hourly data. If you don't need hourly data, select a data set that uses daily data for faster performance.
                </q-tooltip>
              </q-icon>
              <q-icon
                v-if="!scope.opt.hourInterval && scope.opt.name"
                name="today"
                size="sm"
              >
                <q-tooltip>
                  This data set uses daily data. Select daily rather than hourly data sets for faster performance.
                </q-tooltip>
              </q-icon>
            </div>
          </q-item-section>
        </q-item>
      </template>
    </q-select>
    <div
      v-else-if="control === 'output_type'"
    >
      <q-select
        v-model="reportOptions.output_type"
        :disable="disable"
        class="q-my-xs"
        size="xs"
        outline
        emit-value
        map-options
        toggle-color="primary"
        :options="report.optionsForSelect('output_type')"
      />
    </div>
    <div
      v-else-if="control === 'x_axis_output_type'"
    >
      <q-select
        v-model="reportOptions.x_axis_output_type"
        :disable="disable"
        class="q-my-xs"
        size="xs"
        outline
        emit-value
        map-options
        toggle-color="primary"
        :options="report.optionsForSelect('output_type')"
      />
    </div>
    <div
      v-else-if="control === 'y_axis_output_type'"
    >
      <q-select
        v-model="reportOptions.y_axis_output_type"
        :disable="disable"
        class="q-my-xs"
        size="xs"
        outline
        emit-value
        map-options
        toggle-color="primary"
        :options="report.optionsForSelect('output_type')"
      />
    </div>
    <div
      v-else-if="control === 'size_of_bubble'"
    >
      <q-select
        v-model="reportOptions.size_of_bubble"
        :disable="disable"
        class="q-my-xs"
        size="xs"
        outline
        emit-value
        map-options
        toggle-color="primary"
        :options="report.optionsForSelect('output_type')"
      />
    </div>

    <div v-else-if="control === 'group_by'">
      <div v-if="reportOptions.group_by">
        <q-select
          v-model="reportOptions.group_by"
          :disable="disable"
          clearable
          multiple
          use-input
          input-debounce="0"
          use-chips
          style="min-width: 150px; max-width: 250px;"
          label="Group By"
          emit-value
          map-options
          :options="report.groupbyOptionsWithSeparators(groupBySearchValue)"
          @filter="(val, update) => filterSearchValue(val, update, 'groupBySearchValue')"
          @clear="_value => reportOptions.group_by = []"
        >
          <template #option="scope">
            <q-item
              v-if="scope.opt.isSeparator"
              :key="scope.opt.label"
              class="q-py-sm text-uppercase constrain-width-q-item bg-grey-2 text-subtitle2"
              dense
            >
              {{ scope.opt.label }}
            </q-item>
            <q-item
              v-else
              :key="scope.opt.key"
              class="constrain-width-q-item"
              :class="$_.includes(reportOptions.group_by, scope.opt.value) ? 'bg-grey-4 selected-option' : ''"
              v-bind="scope.itemProps"
            >
              <q-item-section>
                <q-item-label class="selected-option-label">
                  {{ scope.opt.label }}
                </q-item-label>
                <q-item-label
                  v-if="scope.opt.definition"
                  caption
                >
                  {{ scope.opt.definition }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>
        <q-btn
          :disable="disable"
          class="q-mt-xs"
          label="Set Order"
          outline
          size="sm"
          @click="$refs.groupByOrderDialog.show()"
        />
        <set-display-order-dialog
          ref="groupByOrderDialog"
          :list="groupByValue"
          @reorder="reorderedList => handleReorder('group_by', reorderedList)"
        />
      </div>
      <q-btn
        v-else
        :disable="disable"
        class="q-mt-xs"
        label="Set Group By"
        outline
        size="sm"
        @click="reportOptions.group_by = []"
      />
    </div>
    <q-select
      v-else-if="control === 'chart_display_type'"
      v-model="reportOptions.chart_display_type"
      :disable="disable"
      style="min-width: 150px; max-width: 250px;"
      label="Data Set"
      :options="report.possibleOptionsValues[control]"
    />
    <div
      v-else-if="control === 'component_width'"
    >
      <q-slider
        v-model.number="reportOptions.component_width"
        :disable="disable"
        label
        type="number"
        :min="1"
        :max="12"
      />
      <q-badge v-if="reportOptions.component_width">
        Width: {{ Math.round(reportOptions.component_width / 12 * 100) }}% of page
      </q-badge>
    </div>
    <div
      v-else-if="control === 'relative_date'"
    >
      <div class="row q-gutter-sm">
        <q-input
          v-model.number="relativeDates.date.amount"
          :disable="disable"
          type="number"
          min="-100"
          max="100"
          step="1"
          @update:model-value="setRelativeDate('date')"
        />
        <q-select
          v-model="relativeDates.date.units"
          :disable="disable"
          :options="['days', 'weeks', 'months', 'years']"
          @update:model-value="setRelativeDate('date')"
        />
      </div>
    </div>
    <div
      v-else-if="control === 'relative_start_date'"
    >
      <div class="row q-gutter-sm">
        <q-input
          v-model.number="relativeDates.start.amount"
          :disable="disable"
          type="number"
          min="-100"
          max="100"
          step="1"
          @update:model-value="setRelativeDate('start')"
        />
        <q-select
          v-model="relativeDates.start.units"
          :disable="disable"
          :options="['days', 'weeks', 'months', 'years']"
          @update:model-value="setRelativeDate('start')"
        />
      </div>
    </div>
    <div
      v-else-if="control === 'relative_end_date'"
    >
      <div class="row q-gutter-sm">
        <q-input
          v-model.number="relativeDates.end.amount"
          :disable="disable"
          type="number"
          min="-100"
          max="100"
          step="1"
          @update:model-value="setRelativeDate('end')"
        />
        <q-select
          v-model="relativeDates.end.units"
          :disable="disable"
          :options="['days', 'weeks', 'months', 'years']"
          @update:model-value="setRelativeDate('end')"
        />
      </div>
    </div>
    <q-input
      v-else-if="control === 'custom_title'"
      v-model="reportOptions.custom_title"
      :disable="disable"
      debounce="750"
      label="Custom Component Title"
    />
    <q-input
      v-else-if="control === 'height'"
      v-model.number="reportOptions.height"
      :disable="disable"
      label="Height"
      type="number"
      min="0"
      max="1000"
      step="1"
    />
    <q-input
      v-else-if="control === 'top_n'"
      v-model.number="reportOptions.top_n"
      :disable="disable"
      label="Top N"
      type="number"
      min="1"
      :max="reportOptions.max_rows"
      step="1"
      debounce="750"
    />
    <q-select
      v-else-if="control === 'output_types'"
      v-model="reportOptions.output_types"
      :disable="disable"
      clearable
      multiple
      use-chips
      style="min-width: 150px; max-width: 250px;"
      label="Outputs"
      :options="report.possibleOptionsValues.output_type"
    />

    <q-select
      v-else-if="control === 'movie_id'"
      v-model="reportOptions.movie_id"
      :disable="disable"
      clearable
      style="min-width: 150px; max-width: 250px;"
      label="Movie"
      emit-value
      map-options
      option-value="id"
      option-label="name"
      :options="$_.map(report.possibleOptionsValues.movie_id, s => $_.pick(s, ['id', 'name']))"
    />
    <div v-else-if="control === 'select_multiple_membership_types'">
      <q-select
        v-model="reportOptions.include_membership_type_ids"
        :disable="disable"
        clearable
        style="min-width: 150px; max-width: 250px;"
        label="Membership Types"
        multiple
        emit-value
        map-options
        use-chips
        option-value="id"
        option-label="name"
        :options="$_.map(membershipTypes.data, s => $_.pick(s, ['id', 'name']))"
      />
    </div>
    <div v-else-if="control === 'select_multiple_movies'">
      <q-select
        v-if="!reportOptions.exclude_movie_ids"
        v-model="reportOptions.include_movie_ids"
        :disable="disable"
        clearable
        style="min-width: 150px; max-width: 250px;"
        label="Movies"
        multiple
        emit-value
        map-options
        use-chips
        option-value="id"
        option-label="name"
        :options="$_.map(report.possibleOptionsValues.movie_id, s => $_.pick(s, ['id', 'name']))"
      />
      <q-select
        v-else
        v-model="reportOptions.exclude_movie_ids"
        :disable="disable"
        clearable
        style="min-width: 150px; max-width: 250px;"
        label="Movies"
        multiple
        emit-value
        map-options
        use-chips
        option-value="id"
        option-label="name"
        :options="$_.map(report.possibleOptionsValues.movie_id, s => $_.pick(s, ['id', 'name']))"
      />
      <q-toggle
        label="Exclude"
        :model-value="!reportOptions.include_movie_ids"
        @update:model-value="toggleIncludeExclude('movie_ids')"
      />
    </div>
    <div v-else-if="control === 'select_multiple_item_classes'">
      <q-select
        v-if="!reportOptions.exclude_item_class_ids"
        v-model="reportOptions.include_item_class_ids"
        :disable="disable"
        clearable
        style="min-width: 150px; max-width: 250px;"
        label="Item Classes"
        multiple
        emit-value
        map-options
        use-chips
        option-value="id"
        option-label="name"
        :options="$_.map(itemClasses.data, s => $_.pick(s, ['id', 'name']))"
      />
      <q-select
        v-else
        v-model="reportOptions.exclude_item_class_ids"
        :disable="disable"
        clearable
        style="min-width: 150px; max-width: 250px;"
        label="Item Classes"
        multiple
        emit-value
        map-options
        use-chips
        option-value="id"
        option-label="name"
        :options="$_.map(itemClasses.data, s => $_.pick(s, ['id', 'name']))"
      />
      <q-toggle
        label="Exclude"
        :model-value="!reportOptions.include_item_class_ids"
        @update:model-value="toggleIncludeExclude('item_class_ids')"
      />
    </div>
    <div v-else-if="control === 'select_multiple_title_classes'">
      <q-select
        v-if="!reportOptions.exclude_title_class_ids"
        v-model="reportOptions.include_title_class_ids"
        :disable="disable"
        clearable
        style="min-width: 150px; max-width: 250px;"
        label="Title Classes"
        multiple
        emit-value
        map-options
        use-chips
        option-value="id"
        option-label="name"
        :options="$_.map(titleClasses.data, s => $_.pick(s, ['id', 'name']))"
      />
      <q-select
        v-else
        v-model="reportOptions.exclude_title_class_ids"
        :disable="disable"
        clearable
        style="min-width: 150px; max-width: 250px;"
        label="Title Classes"
        multiple
        emit-value
        map-options
        use-chips
        option-value="id"
        option-label="name"
        :options="$_.map(titleClasses.data, s => $_.pick(s, ['id', 'name']))"
      />
      <q-toggle
        label="Exclude"
        :model-value="!reportOptions.include_title_class_ids"
        @update:model-value="toggleIncludeExclude('title_class_ids')"
      />
    </div>

    <div v-else-if="control === 'select_multiple_register_stations'">
      <q-select
        v-if="!reportOptions.exclude_register_station_ids"
        v-model="reportOptions.include_register_station_ids"
        :disable="disable"
        clearable
        style="min-width: 150px; max-width: 250px;"
        label="Register Stations"
        multiple
        emit-value
        map-options
        use-chips
        option-value="id"
        option-label="name"
        :options="$_.map(registerStations.data, s => $_.pick(s, ['id', 'name']))"
      />
      <q-select
        v-else
        v-model="reportOptions.exclude_register_station_ids"
        :disable="disable"
        clearable
        style="min-width: 150px; max-width: 250px;"
        label="Register Stations"
        multiple
        emit-value
        map-options
        use-chips
        option-value="id"
        option-label="name"
        :options="$_.map(registerStations.data, s => $_.pick(s, ['id', 'name']))"
      />
      <q-toggle
        label="Exclude"
        :model-value="!reportOptions.include_register_station_ids"
        @update:model-value="toggleIncludeExclude('register_station_ids')"
      />
    </div>
    <div v-else-if="control === 'select_multiple_showing_badges'">
      <showing-badge-select
        :model="reportOptions"
        class="col"
        style="min-width: 150px; max-width: 250px;"
        label="Showing Badges"
        multiple
        :field-name="!reportOptions.exclude_showing_badge_ids ? 'include_showing_badge_ids' : 'exclude_showing_badge_ids'"
        :disable="disable"
      />
      <q-toggle
        label="Exclude"
        :model-value="!reportOptions.include_showing_badge_ids"
        @update:model-value="toggleIncludeExclude('showing_badge_ids')"
      />
    </div>
    <div
      v-else-if="control === 'sort_direction'"
    >
      <q-btn-toggle
        v-model="reportOptions.sort_direction"
        :disable="disable"
        class="q-my-xs"
        size="xs"
        outline
        toggle-color="primary"
        :options="report.optionsForSelect(control)"
      />
    </div>
    <div
      v-else-if="control === 'void_type'"
    >
      <q-btn-toggle
        v-model="reportOptions.void_type"
        :disable="disable"
        class="q-my-xs"
        size="xs"
        outline
        toggle-color="primary"
        :options="report.optionsForSelect(control)"
      />
    </div>
    <div
      v-else-if="control === 'report_level'"
    >
      <q-btn-toggle
        v-model="reportOptions.report_level"
        :disable="disable"
        class="q-my-xs"
        size="sm"
        outline
        toggle-color="primary"
        :options="reportLevelOptions"
      />
    </div>
    <div
      v-else-if="control === 'region_report_level'"
    >
      <q-btn-toggle
        v-model="reportOptions.region_report_level"
        :disable="disable"
        class="q-my-xs"
        size="sm"
        outline
        toggle-color="primary"
        :options="[
          { label: 'Region', value: 'region' },
          { label: 'Circuit', value: 'circuit' },
          { label: 'Site', value: 'site' }
        ]"
      />
    </div>
    <div
      v-else-if="control === 'published_mode'"
    >
      <q-btn-toggle
        v-model="reportOptions.published_mode"
        :disable="disable"
        class="q-my-xs"
        size="sm"
        outline
        toggle-color="primary"
        :options="[
          { label: 'All', value: 'All' },
          { label: 'Published Only', value: 'Published Only' },
          { label: 'Unpublished Only', value: 'Unpublished Only' },
          { label: 'Published Including Future Embargo', value: 'Published Including Future Embargo' },
          { label: 'Unpublished Including Future Embargo', value: 'Unpublished Including Future Embargo' }
        ]"
      />
    </div>
    <div
      v-else-if="control === 'start_end'"
    >
      <q-btn-toggle
        v-model="reportOptions.start_end"
        :disable="disable"
        class="q-my-xs"
        size="sm"
        outline
        toggle-color="primary"
        :options="[
          { label: 'Start Time', value: 'start' },
          { label: 'End Time', value: 'stop' },
          { label: 'Both', value: 'both' }
        ]"
      />
    </div>
    <q-input
      v-else-if="control === 'disclaimer'"
      v-model="reportOptions.disclaimer"
      :disable="disable"
      label="Disclaimer"
      autogrow
    />
    <div
      v-else-if="control === 'footer_image'"
    >
      <image-uploader
        v-model="reportOptions.footer_image"
        min-height="100px"
        :image-url="footerImageUrl"
        :model="reportOptions"
        style="width: 300px;"
      />
    </div>
    <div
      v-else-if="control === 'hide_price_card'"
    >
      <q-checkbox
        v-model="reportOptions.hide_price_card"
        :disable="disable"
        label="Hide Price Card"
      />
    </div>

    <q-input
      v-else-if="control === 'price_card_notes'"
      v-model="reportOptions.price_card_notes"
      :disable="disable"
      label="Price Card Notes"
      autogrow
    />
    <div
      v-else-if="control === 'description_limit'"
    >
      <q-slider
        v-model.number="reportOptions.description_limit"
        :disable="disable"
        label
        type="number"
        :min="0"
        :max="1000"
        :step="1"
        debounce="750"
      />
    </div>
    <q-input
      v-else-if="control === 'custom_description'"
      v-model="reportOptions.custom_description"
      :disable="disable"
      debounce="750"
      label="Custom Description"
      filled
      style="width:300px;"
      autogrow
    />
    <div
      v-else-if="control === 'display_icon'"
    >
      <q-btn
        :icon="reportOptions.display_icon"
        size="lx"
        class="col-auto q-mb-lg q-mt-lg"
        outline
        label="Icon"
        @click="$refs.iconPickerDialog.show()"
      />
      <icon-picker-dialog
        ref="iconPickerDialog"
        :model="reportOptions"
        icon-property="display_icon"
      />
    </div>
    <div
      v-else-if="control === 'rating_type'"
    >
      <q-btn-toggle
        v-model="reportOptions.rating_type"
        :disable="disable"
        class="q-my-xs"
        size="xs"
        outline
        toggle-color="primary"
        :options="report.optionsForSelect('rating_type')"
      />
    </div>
    <q-select
      v-else-if="control === 'employee_id'"
      v-model="reportOptions.employee_id"
      :disable="disable"
      class="sensitive-input"
      clearable
      style="min-width: 150px; max-width: 250px;"
      label="Employee"
      emit-value
      map-options
      use-input
      input-debounce="0"
      :options="filteredEmployeeOptions"
      @filter="(val, update) => filterSearchValue(val, update, 'employeeSearchValue')"
    />
    <q-select
      v-else-if="control === 'register_station'"
      v-model="reportOptions.register_station_id"
      :disable="disable"
      clearable
      style="min-width: 150px; max-width: 250px;"
      label="Register"
      emit-value
      map-options
      option-value="id"
      option-label="name"
      :options="$_.map(registerStations.data, registerStation => $_.pick(registerStation, ['id', 'name']))"
    />
    <q-select
      v-else-if="control === 'sort_by'"
      v-model="reportOptions.sort_by"
      :disable="disable"
      clearable
      style="min-width: 150px; max-width: 250px;"
      label="Sort By"
      emit-value
      map-options
      use-input
      input-debounce="0"
      :options="report.columnNamesForSelect(sortBySearchValue)"
      @filter="(val, update) => filterSearchValue(val, update, 'sortBySearchValue')"
    >
      <template #option="scope">
        <q-item
          v-if="scope.opt.isSeparator"
          :key="scope.opt.label"
          class="q-py-sm text-uppercase constrain-width-q-item bg-grey-2 text-subtitle2"
          dense
        >
          {{ scope.opt.label }}
        </q-item>
        <q-item
          v-else
          :key="scope.opt.key"
          class="constrain-width-q-item"
          v-bind="scope.itemProps"
        >
          <q-item-section>
            <q-item-label>{{ scope.opt.label }}</q-item-label>
            <q-item-label caption>
              {{ scope.opt.definition }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-select>
    <div v-else-if="control === 'column_order'">
      <div v-if="reportOptions.column_order">
        <q-select
          v-model="reportOptions.column_order"
          :disable="disable"
          clearable
          multiple
          use-input
          input-debounce="0"
          use-chips
          style="min-width: 150px; max-width: 250px;"
          label="Columns"
          emit-value
          menu-anchor="top end"
          map-options
          :options="report.columnNamesForSelect(columnSearchValue)"
          @filter="(val, update) => filterSearchValue(val, update, 'columnSearchValue')"
          @clear="_value => reportOptions.column_order = []"
        >
          <template #option="scope">
            <q-item
              v-if="scope.opt.isSeparator"
              :key="'separator-' + scope.opt.key + '-' + scope.opt.label"
              class="q-py-sm text-uppercase constrain-width-q-item bg-grey-2 text-subtitle2"
              dense
            >
              {{ scope.opt.label }}
            </q-item>
            <q-item
              v-else
              :key="'item-' + scope.opt.key + '-' + scope.opt.label"
              class="constrain-width-q-item"
              :class="$_.includes(reportOptions.column_order, scope.opt.value) ? 'bg-grey-4 selected-option' : ''"
              v-bind="scope.itemProps"
            >
              <q-item-section>
                <q-item-label class="selected-option-label">
                  {{ scope.opt.label }}
                </q-item-label>
                <q-item-label
                  v-if="scope.opt.definition"
                  caption
                >
                  {{ scope.opt.definition }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>
        <q-btn
          :disable="disable"
          class="q-mt-xs"
          label="Set Order"
          outline
          size="sm"
          @click="$refs.columnOrderDialog.show()"
        />
        <set-display-order-dialog
          ref="columnOrderDialog"
          :list="reportOptions.column_order || []"
          @reorder="reorderedList => handleReorder('column_order', reorderedList)"
        />
      </div>
      <q-btn
        v-else
        :disable="disable"
        class="q-mt-xs"
        label="Customize Columns"
        outline
        size="sm"
        @click="customizeColumnVisibility()"
      />
    </div>
    <div
      v-else-if="control === 'report_filters'"
    >
      <div v-if="reportOptions.report_filters && report.filterableColumnsOptionsWithSeparators">
        <q-select
          v-model="reportOptions.report_filters"
          :disable="disable"
          clearable
          multiple
          use-input
          input-debounce="0"
          use-chips
          style="min-width: 150px; max-width: 250px;"
          label="Report Filters"
          emit-value
          map-options
          :options="report.filterableColumnsOptionsWithSeparators(reportFiltersSearchValue)"
          @filter="(val, update) => filterSearchValue(val, update, 'reportFiltersSearchValue')"
          @clear="_value => reportOptions.report_filters = []"
        >
          <template #option="scope">
            <q-item
              v-if="scope.opt.isSeparator"
              :key="scope.opt.label"
              class="q-py-sm text-uppercase constrain-width-q-item bg-grey-2 text-subtitle2"
              dense
            >
              {{ scope.opt.label }}
            </q-item>
            <q-item
              v-else
              :key="scope.opt.key"
              class="constrain-width-q-item"
              :class="$_.includes(reportOptions.report_filters, scope.opt.value) ? 'bg-grey-4 selected-option' : ''"
              v-bind="scope.itemProps"
            >
              <q-item-section>
                <q-item-label class="selected-option-label">
                  {{ scope.opt.label }}
                </q-item-label>
                <q-item-label
                  v-if="scope.opt.definition"
                  caption
                >
                  {{ scope.opt.definition }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>
        <q-btn
          :disable="disable"
          class="q-mt-xs"
          label="Set Order"
          outline
          size="sm"
          @click="$refs.reportFiltersOrderDialog.show()"
        />
        <set-display-order-dialog
          ref="reportFiltersOrderDialog"
          :list="reportOptions.report_filters || []"
          @reorder="reorderedList => handleReorder('report_filters', reorderedList)"
        />
      </div>
      <q-btn
        v-else
        :disable="disable"
        class="q-mt-xs"
        label="Set Report Filters"
        outline
        size="sm"
        @click="reportOptions.report_filters = []"
      />
    </div>

    <q-select
      v-else-if="control === 'location_id'"
      v-model="reportOptions.location_id"
      :disable="disable"
      clearable
      style="min-width: 150px; max-width: 250px;"
      label="Location"
      emit-value
      map-options
      :options="locations.data.map(location => ({ label: location.name, value: location.id.toString() }))"
    />
    <div
      v-else-if="control === 'zoom'"
    >
      <div style="min-width: 100px;">
        <div class="row justify-between items-center">
          Zoom
          <q-btn
            v-if="reportOptions.zoom !== 1"
            :disable="disable"
            icon="replay"
            dense
            @click="reportOptions.zoom = 1"
          />
        </div>
        <q-slider
          v-model.number="reportOptions.zoom"
          :disable="disable"
          type="number"
          :min="0.2"
          :max="2"
          :step="0.05"
          label
          debounce="750"
        />
      </div>
    </div>
    <div
      v-else-if="control === 'net_gross'"
    >
      <q-btn-toggle
        v-model="reportOptions.net_gross"
        :disable="disable"
        class="q-my-xs"
        size="xs"
        outline
        toggle-color="primary"
        :options="[
          { label: 'Net', value: 'net' },
          { label: 'Gross', value: 'gross' }
        ]"
      />
    </div>
    <div
      v-else-if="control === 'credit_debit'"
    >
      <q-btn-toggle
        v-model="reportOptions.credit_debit"
        :disable="disable"
        class="q-my-xs"
        size="sm"
        outline
        toggle-color="primary"
        :options="$mh.asOptions(['both', 'debit', 'credit'])"
      />
    </div>
    <effective-type-select
      v-else-if="control === 'effective_type'"
      v-model="reportOptions.effective_type"
      style="min-width: 225px;"
      :disable="disable"
    />
    <div
      v-if="control === 'basis'"
    >
      <q-btn-toggle
        v-model="reportOptions.basis"
        :disable="disable"
        class="q-my-xs"
        size="sm"
        outline
        toggle-color="primary"
        :options="[
          { label: 'Cash Basis', value: 'cash' },
          { label: 'Accrual Basis', value: 'accrual' },
        ]"
      />
    </div>
    <div
      v-if="control === 'tax_mode'"
    >
      <q-btn-toggle
        v-model="reportOptions.tax_mode"
        :disable="disable"
        class="q-my-xs"
        size="sm"
        outline
        toggle-color="primary"
        :options="[
          { label: 'Combined', value: 'combined' },
          { label: 'Cash Basis', value: 'cash_basis' },
          { label: 'Sales Basis', value: 'sales_basis' },
        ]"
      />
    </div>
    <div
      v-else-if="control === 'compact_table'"
    >
      <q-checkbox
        v-model="reportOptions.compact_table"
        :disable="disable"
        label="Spreadsheet Style Table"
      />
    </div>
    <div
      v-else-if="control === 'subtotals'"
    >
      <q-checkbox
        v-model="reportOptions.subtotals"
        :disable="disable"
        label="Show Subtotals"
      />
    </div>
    <div
      v-if="control === 'number_mode'"
    >
      <q-select
        v-model="reportOptions.number_mode"
        :disable="disable"
        class="q-my-xs"
        size="sm"
        outline
        toggle-color="primary"
        :options="NUMBER_MODE_OPTIONS"
        :option-label="(option) => $_.startCase(option)"
        emit-value
      >
        <template #option="{ itemProps, opt, toggleOption }">
          <q-item v-bind="itemProps">
            <q-item-section @click="toggleOption(opt)">
              <q-item-label>{{ opt.label }}</q-item-label>
              <q-item-label caption>
                {{ opt.tooltip }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>
      </q-select>
    </div>
    <div
      v-if="control === 'line_chart_mode'"
    >
      <q-select
        v-model="reportOptions.line_chart_mode"
        :disable="disable"
        class="q-my-xs"
        size="sm"
        outline
        toggle-color="primary"
        :options="LINECHART_MODE_OPTIONS"
        :option-label="(option) => $_.startCase(option)"
        emit-value
      >
        <template #option="{ itemProps, opt, toggleOption }">
          <q-item v-bind="itemProps">
            <q-item-section @click="toggleOption(opt)">
              <q-item-label>{{ opt.label }}</q-item-label>
              <q-item-label caption>
                {{ opt.tooltip }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>
      </q-select>
    </div>
    <div
      v-else-if="control === 'only_show_number'"
    >
      <q-checkbox
        v-model="reportOptions.only_show_number"
        :disable="disable"
        label="Only Show Number"
      />
    </div>
    <div
      v-else-if="control === 'hide_prior_indicators'"
    >
      <q-checkbox
        v-model="reportOptions.hide_prior_indicators"
        :disable="disable"
        label="Hide Prior Indicators"
      />
    </div>
    <div
      v-else-if="control === 'sort_groups'"
    >
      <q-checkbox
        v-model="reportOptions.sort_groups"
        :disable="disable"
        label="Sort Groups"
      />
    </div>
    <div
      v-else-if="isCheckboxControl(control)"
    >
      <q-checkbox
        v-model="reportOptions[control]"
        :disable="disable"
        :label="checkboxLabel(control)"
      />
    </div>
  </div>
</template>

<script>
import { date } from 'quasar'
import { imgixHost } from 'src/config'
import { IsoDate } from 'src/models/model'
import IconPickerDialog from 'src/modules/admin/components/IconPickerDialog.vue'
import ImageUploader from 'src/modules/admin/components/ImageUploader'
import IsoDateInput from 'src/modules/admin/components/IsoDateInput'
import SetDisplayOrderDialog from 'src/modules/admin/components/SetDisplayOrderDialog'
import EffectiveTypeSelect from 'src/modules/admin/effective-type/EffectiveTypeSelect'
import { getOptionsWithSeparators } from 'src/modules/admin/report/ReportUtils'
import ShowingBadgeSelect from 'src/modules/admin/showing-badge/components/ShowingBadgeSelect'

const { formatDate, addToDate } = date

const CONFIGURATION_MODELS = [
  'ticket_type',
  'discount',
  'voucher_type',
  'item',
  'item_variation',
  'price_card_item'
]

const CONFIGURATION_AUDIT_MODELS = [
  'ticket_type',
  'voucher_type',
  'item',
  'site'
]

const FEATURE_NAMES = [
  'all',
  'time_clock',
  'kiosk',
  'membership',
  'consumer',
  'discounts',
  'vouchers',
  'gift_cards',
  'inventory',
  'delivery'
]

const CHECKBOX_CONTROLS = [
  'show_legend',
  'show_bubble_label',
  'percent_stacking',
  'has_custom_title',
  'exclude_zeros',
  'count_only',
  'per_item',
  'per_ticket',
  'per_order',
  'public_only',
  'descending',
  'consolidate_groupings',
  'plain_table_content',
  'report_by_email',
  'show_time_header',
  'show_time_slot_header',
  'show_times',
  'show_time_chips',
  'show_rating',
  'show_seat_chart',
  'show_price_card',
  'show_showing_badges',
  'show_posters',
  'show_progress_bar',
  'has_invoice_date',
  'highlight_starting_showings',
  'color_by_occupancy',
  'highlight_ending_showings',
  'highlight_current_showings',
  'hide_distance_showings',
  'show_relative_times',
  'show_current_time',
  'group_by_tax',
  'group_by_class',
  'group_by_date',
  'group_by_screen',
  'show_full_date_in_showing',
  'group_by_movie',
  'group_by_studio',
  'group_by_showing',
  'group_by_ticket_type_name',
  'group_by_ticket_type_reporting_name',
  'group_by_ticket_type_display_name',
  'group_by_item_class',
  'group_by_item',
  'group_by_item_variation',
  'group_by_price_card'
]

const NUMBER_MODE_OPTIONS = [
  { label: 'Day', value: 'day', tooltip: 'Widget number is day selected' },
  { label: 'Range', value: 'range', tooltip: 'Widget number is sum of range of days selected' }
]

const LINECHART_MODE_OPTIONS = [
  { label: 'Lookback', value: 'lookback', tooltip: 'Line chart looks back the last 60 days from date selected' },
  { label: 'Interval', value: 'interval', tooltip: 'Line chart shows the interval from date range selected' }
]

export default {
  name: 'Report',
  components: {
    ImageUploader,
    IsoDateInput,
    SetDisplayOrderDialog,
    ShowingBadgeSelect,
    EffectiveTypeSelect,
    IconPickerDialog
  },
  props: {
    control: {
      type: String,
      required: true
    },
    report: {
      type: Object,
      required: true
    },
    optionsVariable: {
      type: String,
      required: false,
      default: 'reportOptions'
    },
    disable: {
      type: Boolean,
      required: false,
      default: false
    },
    inventoryRecipes: {
      type: Object,
      required: false,
      default: null
    },
    studios: {
      type: Object,
      required: false,
      default: null
    },
    users: {
      type: Object,
      required: false,
      default: null
    }
  },
  context: ['currentUser'],
  configuration: [
    'taxes',
    'locations',
    'itemClasses',
    'inventoryItems',
    'titleClasses',
    'registerStations',
    'currentCircuit',
    'showingBadges',
    'membershipTypes',
    'currentSite'
  ],
  emits: ['options-changed'],
  data () {
    return {
      columnSearchValue: null,
      groupBySearchValue: null,
      dataSourceSearchValue: null,
      sortBySearchValue: null,
      employeeSearchValue: '',
      reportFiltersSearchValue: null,
      showCalendar: false,
      dateRange: null,
      relativeDates: {
        date: {
          amount: 1,
          units: 'days'
        },
        start: {
          amount: 1,
          units: 'days'
        },
        end: {
          amount: 1,
          units: 'days'
        }
      },
      priorOptions: null,
      isCleaningUpFilters: false
    }
  },
  computed: {
    reportOptions () {
      return this.report[this.optionsVariable]
    },
    reportLevelOptions () {
      // Allow if every site on the circuit has a site in authorized sites matching itself
      if (_.every(this.currentCircuit.sites.data, circuitSite =>
        _.some(this.currentUser.authorizedSites.data, authorizedSite => authorizedSite.id === circuitSite.id)
      )) {
        return [
          { label: 'Site', value: 'site' },
          { label: 'Circuit', value: 'circuit' }
        ]
      }

      return [{ label: 'Site', value: 'site' }]
    },
    footerImageUrl () {
      if (!this.reportOptions.footer_image) return ''

      return `${imgixHost}/${this.reportOptions.footer_image}?w=300&auto=format`
    },
    groupByValue () {
      if (!this.reportOptions.group_by || this.reportOptions.group_by === 'none') return []

      return this.reportOptions.group_by
    },
    circuitEmployeeOptions () {
      return _.map(this.users.data, user => { return { label: user.name, value: user.id } })
    },
    siteEmployeeOptions () {
      return _.map(_.filter(this.users.data, user => _.includes(user.authorizedSiteIds, this.currentSite.id)), user => ({ label: user.name, value: user.id }))
    },
    employeeOptions () {
      return _.values(this.report.optionsForSelect('employee_id')).length > 0 ? this.report.optionsForSelect('employee_id') : this.reportOptions.report_level === 'circuit' ? this.circuitEmployeeOptions : this.siteEmployeeOptions
    },
    filteredEmployeeOptions () {
      return _.filter(this.employeeOptions, option => option.label.toLowerCase().includes(this.employeeSearchValue.toLowerCase()))
    }
  },
  watch: {
    reportOptions: {
      deep: true,
      immediate: true,
      handler () {
        if (!_.isEqual(this.reportOptions, this.priorOptions)) {
          this.showCalendar = false
          this.validateLocalRange()

          this.priorOptions = _.cloneDeep(this.reportOptions)
          this.$emit('options-changed')
        }
      }
    },
    'reportOptions.report_filters': {
      immediate: true,
      deep: true,
      handler (newFilters, oldFilters) {
        if (this.isCleaningUpFilters) return

        if (oldFilters !== undefined) {
          const oldFiltersArray = this.normalizeFilters(oldFilters)
          const newFiltersArray = this.normalizeFilters(newFilters)

          if (oldFiltersArray && oldFiltersArray.length > 0) {
            const removedFilters = oldFiltersArray.filter(filter => !newFiltersArray.includes(filter))
            if (removedFilters.length > 0) {
              this.isCleaningUpFilters = true

              removedFilters.forEach(filterField => {
                if (!_.isNil(this.report.reportOptions[`${filterField}_filter`])) {
                  this.report.reportOptions[`${filterField}_filter`] = null
                  this.report.defaultOptions[`${filterField}_filter`] = null
                }
                if (!_.isNil(this.report.reportOptions[`exclude_${filterField}`])) {
                  this.report.reportOptions[`exclude_${filterField}`] = null
                  this.report.defaultOptions[`exclude_${filterField}`] = null
                }
              })

              this.isCleaningUpFilters = false
            }
          }
        }
      }
    },
    dateRange () {
      if (this.dateRange) {
        this.showCalendar = false
        this.validateReportRange()
      }
    },
    'dateRange.from' () {
      this.validateReportRange()
    },
    'dateRange.to' () {
      this.validateReportRange()
    },
    'reportOptions.relative_date': {
      immediate: true,
      handler () {
        this.validateRelativeDateControls('date')
      }
    },
    'reportOptions.relative_start_date': {
      immediate: true,
      handler () {
        this.validateRelativeDateControls('start')
      }
    },
    'reportOptions.relative_end_date': {
      immediate: true,
      handler () {
        this.validateRelativeDateControls('end')
      }
    }
  },
  created () {
    _.extend(this, { addToDate, formatDate, imgixHost, IsoDate, NUMBER_MODE_OPTIONS, LINECHART_MODE_OPTIONS, CONFIGURATION_MODELS, CONFIGURATION_AUDIT_MODELS, FEATURE_NAMES })

    this.priorOptions = _.cloneDeep(this.reportOptions)
  },
  methods: {
    normalizeFilters (filters) {
      if (typeof filters === 'string') {
        return filters.split(',').map(item => item.trim()).filter(item => item.length > 0)
      } else if (Array.isArray(filters)) {
        return filters
      } else {
        return []
      }
    },
    dataSourceNameOptionsAndSeparators (datasourceSearchValue) {
      return getOptionsWithSeparators(
        this.report.possibleOptionsValues.data_source_name_options,
        [],
        datasourceSearchValue
      )
    },
    filterSearchValue (val, update, searchField) {
      update(() => {
        this[searchField] = val.toLowerCase()
      })
    },
    async validateLocalRange () {
      this.silentDate = true

      if (this.reportOptions.start_date && this.reportOptions.end_date) {
        this.dateRange = {
          from: this.reportOptions.start_date,
          to: this.reportOptions.end_date
        }
      } else {
        this.dateRange = null
      }
      await this.$nextTick()
      this.silentDate = false
    },
    validateReportRange () {
      if (this.silentDate) return

      this.reportOptions.start_date = this.dateRange ? (this.dateRange.from || this.dateRange) : null
      this.reportOptions.end_date = this.dateRange ? (this.dateRange.to || this.dateRange) : null
      this.$emit('options-changed')
    },
    validateRelativeDateControls (field) {
      if (this.silentRange) return

      const fieldName = field === 'date' ? 'relative_date' : `relative_${field}_date`

      if (!this.reportOptions[fieldName]) {
        this.relativeDates[field].amount = null
        this.relativeDates[field].units = 'days'
        return
      }

      const relativeValues = this.reportOptions[fieldName].split('_')

      this.relativeDates[field].amount = parseInt(relativeValues[1]) * (relativeValues[0] === 'minus' ? -1 : 1)
      this.relativeDates[field].units = relativeValues[2]
    },
    async setRelativeDate (field) {
      this.silentRange = true
      const values = this.relativeDates[field]
      const fieldName = field === 'date' ? 'relative_date' : `relative_${field}_date`
      this.reportOptions[fieldName] = `${values.amount < 0 ? 'minus' : 'plus'}_${Math.abs(values.amount)}_${values.units}`
      await this.$nextTick()
      this.silentRange = false
    },
    toggleIncludeExclude (fieldName) {
      if (this.reportOptions[`include_${fieldName}`]) {
        this.reportOptions[`include_${fieldName}`] = null
        this.reportOptions[`exclude_${fieldName}`] = []
      } else {
        this.reportOptions[`include_${fieldName}`] = []
        this.reportOptions[`exclude_${fieldName}`] = null
      }
    },
    customizeColumnVisibility () {
      if (!this.reportOptions.column_order) {
        this.reportOptions.column_order = _.map(this.report.columnNamesForSelect, 'value')
      }
    },
    handleReorder (field, { reorderedList }) {
      this.reportOptions[field] = reorderedList
    },
    isCheckboxControl (control) {
      return _.includes(CHECKBOX_CONTROLS, control)
    },
    checkboxLabel (control) {
      return _.startCase(control)
    }
  }
}
</script>

<style lang="scss">

.constrain-width-q-item
{
  max-width:300px;
}

.constrain-width-q-item--lg
{
  max-width: 400px;
}

.selected-option .selected-option-label {
  color: var(--q-primary) !important;
}
</style>
